import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { StatCard } from "@/components/admin/StatCard";
import { InviteResults } from "@/components/admin/InviteResults";
import { Loader2, Send, Eye, EyeOff } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS = {
  invited: { label: "Invited", tone: "border-border text-muted-foreground" },
  claim_pending: { label: "Claim pending", tone: "border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400" },
  claimed: { label: "Claimed listing", tone: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  listing_requested: { label: "Requested a listing", tone: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400" },
  listed: { label: "Listed", tone: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  opted_out: { label: "Opted out", tone: "border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400" },
  opened: { label: "Opened join page", tone: "border-accent/30 bg-accent/10 text-accent" },
};

const fmtDate = (d) => new Date(d).toLocaleDateString();

const OpenedInfo = ({ r }) => (r.open_count ? (
  <span className="inline-flex items-center gap-1 text-[0.7rem] font-semibold text-accent" data-testid={`invite-opened-${r.email}`}
    title={`First visit ${new Date(r.first_opened_at || r.last_opened_at).toLocaleString()} · last visit ${new Date(r.last_opened_at).toLocaleString()}`}>
    <Eye size={12} /> Opened {r.open_count}× · last {fmtDate(r.last_opened_at)}
  </span>
) : <span className="inline-flex items-center gap-1 text-[0.7rem] text-muted-foreground" data-testid={`invite-not-opened-${r.email}`}><EyeOff size={12} /> Not opened</span>);

const selectCls = "h-9 rounded-sm border border-input bg-background px-2 text-sm";

const FeaturedTable = ({ spots }) => (
  <div className="grid-card overflow-x-auto mb-10" data-testid="admin-featured-table">
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
        <tr><th className="p-3">Lawyer</th><th className="p-3">County</th><th className="p-3">Status</th><th className="p-3">Since</th><th className="p-3 text-right">Views</th><th className="p-3 text-right">Calls</th><th className="p-3 text-right">Web</th></tr>
      </thead>
      <tbody className="divide-y divide-border">
        {spots.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No featured spots yet.</td></tr>}
        {spots.map((s) => (
          <tr key={s.id} data-testid={`admin-featured-row-${s.id}`}>
            <td className="p-3"><p className="font-medium">{s.attorney_name}</p><p className="text-xs text-muted-foreground">{s.firm || s.email}</p></td>
            <td className="p-3">{s.county}</td>
            <td className="p-3"><span className={`text-xs font-semibold ${s.status === "active" ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>{s.status}</span></td>
            <td className="p-3 text-muted-foreground">{s.started_at ? new Date(s.started_at).toLocaleDateString() : "—"}</td>
            <td className="p-3 text-right">{s.stats.views}</td><td className="p-3 text-right">{s.stats.calls}</td><td className="p-3 text-right">{s.stats.web_clicks}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Invites = () => {
  const [info, setInfo] = useState(null);
  const [text, setText] = useState("");
  const [useDir, setUseDir] = useState(false);
  const [busy, setBusy] = useState(false);
  const [area, setArea] = useState("Landlord/Tenant");
  const [lang, setLang] = useState("en");
  const load = useCallback(() => api.get("/admin/invites", { params: { area } }).then(({ data }) => setInfo(data)).catch(() => {}), [area]);
  useEffect(() => { load(); }, [load]);
  const emails = text.split(/[\s,;]+/).map((e) => e.trim()).filter((e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", { emails, include_directory: useDir, area, lang });
      toast.success(`Sent ${data.sent} invite${data.sent === 1 ? "" : "s"}${data.skipped_recent ? ` · ${data.skipped_recent} already invited in the last 30 days` : ""}${data.skipped_claimed ? ` · ${data.skipped_claimed} already joined` : ""}`);
      if (data.failed?.length) toast.error(`Failed: ${data.failed.join(", ")}`);
      setText(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send invites"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid-card p-6" data-testid="admin-invites">
      <p className="font-serif text-2xl text-foreground mb-1">Invite lawyers to the directory</p>
      <p className="text-sm text-muted-foreground mb-5">
        {info ? `${info.area_listings} ${area} listing${info.area_listings === 1 ? "" : "s"} in the directory today.` : "…"} Invites link to a personal join page. Nobody is invited twice within 30 days.
      </p>
      <div className="flex flex-wrap gap-4 mb-4">
        <label className="text-xs space-y-1"><span className="block text-muted-foreground">Practice area</span>
          <select value={area} onChange={(e) => setArea(e.target.value)} className={selectCls} data-testid="admin-invites-area">
            {(info?.areas || [area]).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="text-xs space-y-1"><span className="block text-muted-foreground">Email & join page language</span>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className={selectCls} data-testid="admin-invites-lang">
            <option value="en">English</option><option value="es">Spanish</option>
          </select>
        </label>
      </div>
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste emails, separated by commas or new lines"
        data-testid="admin-invites-emails" />
      <p className="text-xs text-muted-foreground mt-1">{emails.length} valid email{emails.length === 1 ? "" : "s"}</p>
      <label className="flex items-center gap-2 text-sm mt-4">
        <Checkbox checked={useDir} onCheckedChange={(v) => setUseDir(!!v)} data-testid="admin-invites-directory" />
        Also email unclaimed {area} lawyers already in the directory ({info?.directory_count ?? 0} with an email on file)
      </label>
      <Button onClick={send} disabled={busy || (!emails.length && !(useDir && info?.directory_count))} className="rounded-sm gap-2 mt-5" data-testid="admin-invites-send">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send invites
      </Button>
      {info?.recent?.length > 0 && (
        <div className="mt-6" data-testid="admin-invites-recent">
          <div className="flex flex-wrap gap-2 mb-3" data-testid="admin-invites-followup">
            {Object.entries(info.follow_up || {}).filter(([, n]) => n > 0).map(([k, n]) => (
              <span key={k} className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS[k].tone}`} data-testid={`invites-count-${k}`}>{n} {STATUS[k].label}</span>
            ))}
          </div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Invited lawyers</p>
          <ul className="text-sm divide-y divide-border max-h-72 overflow-y-auto">
            {info.recent.map((r) => (
              <li key={`${r.email}-${r.sent_at}`} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`invite-row-${r.email}`}>
                <span className="min-w-0">{r.email}{r.listing && <span className="text-xs text-muted-foreground"> · {r.listing}</span>}
                  <span className="block text-xs text-muted-foreground" data-testid={`invite-area-${r.email}`}>{r.area || "Landlord/Tenant"} · {(r.lang || "en") === "es" ? "Spanish" : "English"}</span>
                </span>
                <span className="flex flex-wrap items-center gap-3">
                  <OpenedInfo r={r} />
                  <span className={`rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold ${STATUS[r.status]?.tone || ""}`} data-testid={`invite-status-${r.email}`}>{STATUS[r.status]?.label || r.status}</span>
                  <span className="text-muted-foreground text-xs">Sent {fmtDate(r.sent_at)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function AdminFeatured() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/featured/summary").then(({ data }) => setData(data)).catch(() => toast.error("Could not load featured spots")); }, []);
  return (
    <div data-testid="admin-featured">
      <h1 className="font-serif text-4xl tracking-tight mb-6">Featured lawyers</h1>
      {!data ? <p className="text-muted-foreground animate-pulse">Loading…</p> : (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <StatCard label="Monthly revenue" value={money(data.monthly_revenue)} sub={`${money(data.price)} per county`} accent testid="admin-featured-revenue" />
            <StatCard label="Active spots" value={data.active_count} sub={`up to ${data.slots} per county`} testid="admin-featured-active" />
            <StatCard label="Counties with featured lawyers" value={data.counties.length} sub={data.counties.map((c) => `${c.county} ${c.taken}/${data.slots}`).join(" · ") || "none yet"} testid="admin-featured-counties" />
          </div>
          <FeaturedTable spots={data.spots} />
        </>
      )}
      <InviteResults />
      <Invites />
    </div>
  );
}
